import { isFunction, isObject } from "@ciaopeople/cp-shatsu/TypeChecking";
import { VideoInfoRawData } from "../../../model/video/VideoInfo.js";
import { AdUnit, ResolvedAdUnit } from "../AdUnit.js";
import { AdCallParams } from "../AdCallParams.js";
import { CustomParamDictionary, CustomParams, StandardCustomParamDictionary } from "./CustomParams.js";
import { appendInQs, encodeQS } from "../../../lib/utils/QueryStringUtils.js";
import { getContestoBrowser } from "@ciaopeople/cp-shatsu/ClientDetection";
import { CustomWindow } from "@ciaopeople/cp-shatsu/Ads";


export type AdCallParamsFilter = (unit: AdUnit, info: VideoInfoRawData, currentParams: AdCallParams) => AdCallParams


export interface HasAdCallParamsFilter {

    filterAdCall: AdCallParamsFilter
}


function hasAdCallParamsFilter(a: any): a is HasAdCallParamsFilter {
    return ("filterAdCall" in a) && isFunction(a.filterAdCall);
}


export type PlaceholderReplacer = [string, (s: string) => string]


type FilterPriority = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;


const CUST_PARAMS_SERACH_REGEX = /(?:cust_params=)([^&\n]*)|(?:cust_params=?$)/i

export class ComposableResolver {

    private replacers: PlaceholderReplacer[];
    private filters: [FilterPriority, AdCallParamsFilter][];

    constructor(private dictionary: CustomParamDictionary = StandardCustomParamDictionary) {

        this.replacers = [];
        this.filters = [];
    }


    registerReplacer(p: PlaceholderReplacer): void {

        this.replacers.push(p);
    }


    addParamsFilter(flt: AdCallParamsFilter | HasAdCallParamsFilter, prio: FilterPriority = 5) {

        const current = this.filters.slice();
        if (hasAdCallParamsFilter(flt)) {
            current.push([prio, (a, b, c) => flt.filterAdCall(a, b, c)])
        }
        else {
            current.push([prio, flt]);
        }

        this.filters = current.sort((a, b) => a[0] - b[0]);

    }




    resolver(unit: AdUnit, info: VideoInfoRawData, startingParams: AdCallParams = {}): ResolvedAdUnit {

        const initialParams: AdCallParams = isObject(startingParams) ? startingParams : {};


        const copiedUnit: AdUnit = {
            baseUrl: unit.baseUrl,
            label: unit.label,
            unitType: unit.unitType
        };

        copiedUnit.baseUrl = this.replacers.reduce((url: string, replacer: PlaceholderReplacer) => {

            const [placeholder, replaceFun] = replacer;
            const regex = new RegExp(`\\[${placeholder}\\]`, 'g');
            return url.replace(regex, replaceFun(placeholder));

        }, unit.baseUrl)


        const finalParams = this.filters.reduce((prev, filter) => filter[1](copiedUnit, info, prev), initialParams);
        const translatedParams = finalParams.custom_params ? applyDictionary(finalParams.custom_params, this.dictionary) : null;

        let custom_params = translatedParams ? encodeURIComponent(encodeQS(translatedParams, true)) : "";
        if (finalParams.pre_serialized_custom_params &&
            finalParams.pre_serialized_custom_params.length > 0) {
            const joined = finalParams.pre_serialized_custom_params.join(encodeURIComponent("&"));
            custom_params = (custom_params !== "" ? custom_params + encodeURIComponent("&") : "") + joined;
        }

        const toConvert: { [k: string]: any } = finalParams.other_params ? { ...finalParams.other_params } : {};
        if (finalParams.pod) toConvert.pod = finalParams.pod;
        if (finalParams.placement) {
            toConvert['plcmt'] = finalParams.placement;
        }


        if (finalParams.description_url) toConvert.description_url = finalParams.description_url;

        copiedUnit.baseUrl = appendInQs(copiedUnit.baseUrl, encodeQS(toConvert, true));

        if (custom_params.trim() !== "") {

            const matches = copiedUnit.baseUrl.match(CUST_PARAMS_SERACH_REGEX);
            if (matches && matches.length > 1) {
                const reminder = matches[1].startsWith("[") ? "" : matches[1] + encodeURIComponent("&");
                copiedUnit.baseUrl = copiedUnit.baseUrl.replace(CUST_PARAMS_SERACH_REGEX, "cust_params=" + reminder + custom_params);
            }

            else copiedUnit.baseUrl = appendInQs(copiedUnit.baseUrl, `cust_params=${custom_params}`);
        }


        return {
            ...unit,
            parametrizedUrl: copiedUnit.baseUrl
        };
    }


}




export function applyDictionary(params: CustomParams, dict: CustomParamDictionary): { [k: string]: string } {

    const acc: { [k: string]: string } = {};
    for (const [k, v] of Object.entries(params)) {
        acc[dict[k]] = v + "";
    }

    return acc;

}




